import type { TimelineEntry } from '../../api';

/** A message the channel posted (or one revision of it), verbatim. */
type ChannelMessageEntry = Extract<TimelineEntry, { kind: 'CHANNEL_MESSAGE' }>;
/** What FXMate understood from one message revision (structured, never free text: R7). */
export type InterpretationEntry = Extract<TimelineEntry, { kind: 'INTERPRETATION' }>;
/** What the engine did after an interpretation. */
export type ExecutionEntry = Extract<TimelineEntry, { kind: 'EXECUTION' }>;

/**
 * What the channel did to the message of a step, shown as a label next to it (FXM-03 §4
 * "Corrections and deletions", spec R6): `edited` (revision > 0), `reply` (`replyToMessageId`),
 * `deleted` (`deleted`, C1 decision 7). Always in this order.
 */
type StepMarker = 'edited' | 'reply' | 'deleted';

/**
 * One row of the timeline: a channel message, what FXMate understood from it and what was
 * executed, side by side (FXM-03 §4 "What the trader sees", I10).
 */
export interface TimelineStep {
  /** Stable React key: `messageId#revision`, or the entry position for an orphan execution. */
  readonly key: string;
  readonly message: ChannelMessageEntry | null;
  readonly interpretation: InterpretationEntry | null;
  readonly executions: readonly ExecutionEntry[];
  /** Labels of the message; empty for a step without a message. */
  readonly markers: readonly StepMarker[];
}

interface MutableStep {
  key: string;
  message: ChannelMessageEntry | null;
  interpretation: InterpretationEntry | null;
  executions: ExecutionEntry[];
}

/**
 * Pairs the flat entries of `GET /positions/{id}/timeline` into steps (spec §5):
 *
 * - a `CHANNEL_MESSAGE` and an `INTERPRETATION` belong together when their `messageId` and
 *   `revision` match (C1: "paired with its message by messageId and revision");
 * - an `EXECUTION` carries no message id, so it belongs to the step touched last before it, in
 *   time order (the instruction it executed).
 *
 * Edge: an `INTERPRETATION` of a revision > 0 with no matching `CHANNEL_MESSAGE` makes a step
 * without a message, so the screen shows no "edited" label for it (the label reads the message).
 *
 * Entries are taken in `at` order (the channel's publication order, FXM-04 §6 case 13). The API
 * already sorts them (AC7); sorting again here costs nothing and keeps the pairing right if it
 * does not. Equal times keep their order (stable sort).
 */
export function toTimelineSteps(entries: readonly TimelineEntry[]): TimelineStep[] {
  const ordered = entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => Date.parse(a.entry.at) - Date.parse(b.entry.at) || a.index - b.index);
  const steps: MutableStep[] = [];
  const byKey = new Map<string, MutableStep>();
  let current: MutableStep | undefined;

  const stepFor = (key: string): MutableStep => {
    const existing = byKey.get(key);
    if (existing !== undefined) return existing;
    const created: MutableStep = { key, message: null, interpretation: null, executions: [] };
    byKey.set(key, created);
    steps.push(created);
    return created;
  };

  for (const { entry, index } of ordered) {
    switch (entry.kind) {
      case 'CHANNEL_MESSAGE':
        current = stepFor(`${entry.messageId}#${String(entry.revision)}`);
        current.message = entry;
        break;
      case 'INTERPRETATION':
        current = stepFor(`${entry.messageId}#${String(entry.revision)}`);
        current.interpretation = entry;
        break;
      case 'EXECUTION':
        current ??= stepFor(`execution-${String(index)}`);
        current.executions.push(entry);
        break;
    }
  }
  return steps.map((step) => ({ ...step, markers: markersOf(step.message) }));
}

/** The {@link StepMarker}s of a message, in display order. */
function markersOf(message: ChannelMessageEntry | null): StepMarker[] {
  if (message === null) return [];
  const markers: StepMarker[] = [];
  if (message.revision > 0) markers.push('edited');
  if (message.replyToMessageId !== null) markers.push('reply');
  if (message.deleted) markers.push('deleted');
  return markers;
}
